import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Switch, Select, Upload, Button, Typography, Row, Col, Divider, Slider, Space, Image, message } from 'antd';
import { UploadOutlined, FileImageOutlined, PlusOutlined, MinusCircleOutlined, DeleteOutlined, BoldOutlined } from '@ant-design/icons';
import { useDoctorContext } from './DoctorContext';
import { useClinicContext } from './ClinicContext';
import { getRxPrintArPatientFreqOnEnPrescription, setRxPrintArPatientFreqOnEnPrescription } from '../utils/prescriptionPrintLocalSettings';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface HeaderLine {
    text: string;
    fontSize: number;
    bold: boolean;
}

interface HeaderConfig {
    enabled: boolean;
    leftLines: HeaderLine[];
    centerBadgeBase64: string;
    centerBadgeSize: number;
    centerBadgeWidth: number;
    centerBadgeHeight: number;
    headerHeight: number;
    rightLines: HeaderLine[];
}

interface FooterLine {
    text: string;
    fontSize: number;
    bold: boolean;
}

interface FooterConfig {
    enabled: boolean;
    lines: FooterLine[];
}

const DEFAULT_FONT_SIZE = 14;
const DEFAULT_BADGE_SIZE = 80;

const migrateLine = (raw: any, index: number): HeaderLine => {
    if (typeof raw === 'string') {
        return { text: raw, fontSize: index === 0 ? 18 : 13, bold: index === 0 };
    }
    return { text: raw.text || '', fontSize: raw.fontSize || DEFAULT_FONT_SIZE, bold: !!raw.bold };
};

const migrateFooterLine = (raw: any): FooterLine => {
    if (typeof raw === 'string') {
        return { text: raw, fontSize: 12, bold: false };
    }
    return { text: raw.text || '', fontSize: raw.fontSize || 12, bold: !!raw.bold };
};

const migrateLines = (lines: any[] | undefined, fallback: HeaderLine[]): HeaderLine[] => {
    if (!lines || lines.length === 0) return fallback;
    return lines.map((l, i) => migrateLine(l, i));
};

const migrateFooterLines = (lines: any[] | undefined, fallback: FooterLine[]): FooterLine[] => {
    if (!lines || lines.length === 0) return fallback;
    return lines.map(l => migrateFooterLine(l));
};

const PrescriptionLayoutEditor: React.FC = () => {
    const { settings, updateSettings, doctorId } = useDoctorContext();
    const { selectedClinicId } = useClinicContext();
    const [backgroundObjectUrl, setBackgroundObjectUrl] = useState<string | null>(null);
    /** Visual preview: standard drug list vs OB/GYN two-column + investigations */
    const [previewRxTemplate, setPreviewRxTemplate] = useState<'standard' | 'obgyn'>('standard');
    const [localArPatientFreqOnEnPrint, setLocalArPatientFreqOnEnPrint] = useState(() => getRxPrintArPatientFreqOnEnPrescription());
    const [canUseOwnerPrintSettings, setCanUseOwnerPrintSettings] = useState(false);
    const [customCss, setCustomCss] = useState(settings.printSettings?.prescriptionCustomCss || '');
    const [customHtml, setCustomHtml] = useState((settings.printSettings as any)?.prescriptionCustomHtml || '');

    const emptyHeaderLine: HeaderLine = { text: '', fontSize: DEFAULT_FONT_SIZE, bold: false };
    const emptyFooterLine: FooterLine = { text: '', fontSize: 12, bold: false };

    const [previewSettings, setPreviewSettings] = useState({
        paperSize: settings.printSettings?.paperSize || 'a4',
        marginTop: settings.printSettings?.marginTop || 0,
        marginLeft: settings.printSettings?.marginLeft || 0,
        marginRight: settings.printSettings?.marginRight || 0,
        showHeader: settings.printSettings?.showHeader !== false,
        showFooter: settings.printSettings?.showFooter !== false,
        showPatientInfo: settings.printSettings?.showPatientInfo !== false,
        printLocale: settings.printSettings?.printLocale || 'en',
        physicianPrintOrientation: settings.printSettings?.physicianPrintOrientation || 'landscape',
    });

    const rxH = settings.printSettings?.prescriptionHeader;
    const rxF = settings.printSettings?.prescriptionFooter;

    const [headerConfig, setHeaderConfig] = useState<HeaderConfig>({
        enabled: !!rxH?.enabled,
        leftLines: migrateLines(rxH?.leftLines, [emptyHeaderLine]),
        centerBadgeBase64: rxH?.centerBadgeBase64 || '',
        centerBadgeSize: (rxH as any)?.centerBadgeSize || DEFAULT_BADGE_SIZE,
        centerBadgeWidth: (rxH as any)?.centerBadgeWidth || (rxH as any)?.centerBadgeSize || DEFAULT_BADGE_SIZE,
        centerBadgeHeight: (rxH as any)?.centerBadgeHeight || 0,
        headerHeight: (rxH as any)?.headerHeight || 0,
        rightLines: migrateLines(rxH?.rightLines, [emptyHeaderLine]),
    });

    const [footerConfig, setFooterConfig] = useState<FooterConfig>({
        enabled: !!rxF?.enabled,
        lines: migrateFooterLines(rxF?.lines, [emptyFooterLine]),
    });

    useEffect(() => {
        let cancelled = false;

        const checkOwnerPrintAvailability = async () => {
            const currentDoctorId = doctorId || localStorage.getItem('doctorId');
            if (!currentDoctorId || !selectedClinicId || selectedClinicId === '__all_clinics__') {
                if (!cancelled) setCanUseOwnerPrintSettings(false);
                return;
            }

            const ownerDoctorId = await getClinicOwnerDoctorId(selectedClinicId, currentDoctorId);
            if (!cancelled) {
                setCanUseOwnerPrintSettings(!!ownerDoctorId && ownerDoctorId !== currentDoctorId);
            }
        };

        checkOwnerPrintAvailability();
        return () => {
            cancelled = true;
        };
    }, [doctorId, selectedClinicId]);

    useEffect(() => {
        const rh = settings.printSettings?.prescriptionHeader;
        const rf = settings.printSettings?.prescriptionFooter;

        setPreviewSettings({
            paperSize: settings.printSettings?.paperSize || 'a4',
            marginTop: settings.printSettings?.marginTop || 0,
            marginLeft: settings.printSettings?.marginLeft || 0,
            marginRight: settings.printSettings?.marginRight || 0,
            showHeader: settings.printSettings?.showHeader !== false,
            showFooter: settings.printSettings?.showFooter !== false,
            showPatientInfo: settings.printSettings?.showPatientInfo !== false,
            printLocale: settings.printSettings?.printLocale || 'en',
            physicianPrintOrientation: settings.printSettings?.physicianPrintOrientation || 'landscape',
        });
        setCustomCss(settings.printSettings?.prescriptionCustomCss || '');
        setCustomHtml((settings.printSettings as any)?.prescriptionCustomHtml || '');

        setHeaderConfig({
            enabled: !!rh?.enabled,
            leftLines: migrateLines(rh?.leftLines, [emptyHeaderLine]),
            centerBadgeBase64: rh?.centerBadgeBase64 || '',
            centerBadgeSize: (rh as any)?.centerBadgeSize || DEFAULT_BADGE_SIZE,
            centerBadgeWidth: (rh as any)?.centerBadgeWidth || (rh as any)?.centerBadgeSize || DEFAULT_BADGE_SIZE,
            centerBadgeHeight: (rh as any)?.centerBadgeHeight || 0,
            headerHeight: (rh as any)?.headerHeight || 0,
            rightLines: migrateLines(rh?.rightLines, [emptyHeaderLine]),
        });

        setFooterConfig({
            enabled: !!rf?.enabled,
            lines: migrateFooterLines(rf?.lines, [emptyFooterLine]),
        });
    }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

    const useOwnerPrintSettings =
        canUseOwnerPrintSettings && (settings.printSettings as any)?.useOwnerPrintSettings !== false;

    const handleUseOwnerPrintSettingsChange = (enabled: boolean) => {
        updateSettings({
            ...settings,
            printSettings: {
                paperSize: settings.printSettings?.paperSize || 'a4',
                marginTop: settings.printSettings?.marginTop || 0,
                marginLeft: settings.printSettings?.marginLeft || 0,
                marginRight: settings.printSettings?.marginRight || 0,
                showHeader: settings.printSettings?.showHeader !== false,
                showFooter: settings.printSettings?.showFooter !== false,
                showPatientInfo: settings.printSettings?.showPatientInfo !== false,
                printLocale: settings.printSettings?.printLocale || 'en',
                physicianPrintOrientation: settings.printSettings?.physicianPrintOrientation || 'landscape',
                prescriptionHeader: settings.printSettings?.prescriptionHeader,
                prescriptionFooter: settings.printSettings?.prescriptionFooter,
                prescriptionCustomCss: settings.printSettings?.prescriptionCustomCss,
                prescriptionCustomHtml: (settings.printSettings as any)?.prescriptionCustomHtml,
                useOwnerPrintSettings: enabled,
            } as any,
        });
    };

    const handleSettingChange = (key: string, value: any) => {
        setPreviewSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        const cleanLeft = headerConfig.leftLines.filter(l => l.text.trim() !== '');
        const cleanRight = headerConfig.rightLines.filter(l => l.text.trim() !== '');
        const cleanFooter = footerConfig.lines.filter(l => l.text.trim() !== '');

        updateSettings({
            ...settings,
            printSettings: {
                paperSize: previewSettings.paperSize as any,
                marginTop: previewSettings.marginTop,
                marginLeft: previewSettings.marginLeft,
                marginRight: previewSettings.marginRight,
                showHeader: previewSettings.showHeader,
                showFooter: previewSettings.showFooter,
                showPatientInfo: previewSettings.showPatientInfo,
                printLocale: previewSettings.printLocale as 'en' | 'ar',
                physicianPrintOrientation: previewSettings.physicianPrintOrientation as 'landscape' | 'portrait',
                useOwnerPrintSettings: (settings.printSettings as any)?.useOwnerPrintSettings,
                prescriptionCustomCss: customCss,
                prescriptionCustomHtml: customHtml,
                prescriptionHeader: headerConfig.enabled
                    ? {
                        enabled: true,
                        leftLines: cleanLeft.length > 0 ? cleanLeft : [emptyHeaderLine],
                        centerBadgeBase64: headerConfig.centerBadgeBase64,
                        centerBadgeSize: headerConfig.centerBadgeSize,
                        centerBadgeWidth: headerConfig.centerBadgeWidth,
                        centerBadgeHeight: headerConfig.centerBadgeHeight,
                        headerHeight: headerConfig.headerHeight,
                        rightLines: cleanRight.length > 0 ? cleanRight : [emptyHeaderLine],
                    } as any
                    : { enabled: false, leftLines: [], centerBadgeBase64: '', centerBadgeWidth: DEFAULT_BADGE_SIZE, centerBadgeHeight: 0, headerHeight: 0, rightLines: [] },
                prescriptionFooter: footerConfig.enabled
                    ? {
                        enabled: true,
                        lines: cleanFooter.length > 0 ? cleanFooter : [emptyFooterLine],
                    } as any
                    : { enabled: false, lines: [] },
            }
        });
    };

    const handleImageUpload = (file: File) => {
        const objectUrl = URL.createObjectURL(file);
        setBackgroundObjectUrl(objectUrl);
        return false;
    };

    const extractCssFromUploadedText = (text: string) => {
        const styleMatch = text.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
        return styleMatch ? styleMatch[1].trim() : text.trim();
    };

    const handleCustomCssUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = e => {
            setCustomCss(extractCssFromUploadedText(String(e.target?.result || '')));
            message.success('Prescription style uploaded');
        };
        reader.readAsText(file);
        return false;
    };

    const handleCustomHtmlUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = e => {
            setCustomHtml(String(e.target?.result || '').trim());
            message.success('Prescription HTML template uploaded');
        };
        reader.readAsText(file);
        return false;
    };

    const processAndSetBadge = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new window.Image();
            img.onload = () => {
                const maxSize = 900;
                let w = img.width;
                let h = img.height;
                if (w > maxSize || h > maxSize) {
                    const ratio = Math.min(maxSize / w, maxSize / h);
                    w = Math.round(w * ratio);
                    h = Math.round(h * ratio);
                }
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d')!;
                ctx.drawImage(img, 0, 0, w, h);
                setHeaderConfig(prev => ({ ...prev, centerBadgeBase64: canvas.toDataURL('image/png') }));
                message.success('Badge uploaded');
            };
            img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
        return false;
    };

    // --- Header line helpers ---
    const updateHeaderLineField = (side: 'leftLines' | 'rightLines', index: number, field: keyof HeaderLine, value: any) => {
        setHeaderConfig(prev => {
            const lines = [...prev[side]];
            lines[index] = { ...lines[index], [field]: value };
            return { ...prev, [side]: lines };
        });
    };

    const addHeaderLine = (side: 'leftLines' | 'rightLines') => {
        setHeaderConfig(prev => ({ ...prev, [side]: [...prev[side], { ...emptyHeaderLine }] }));
    };

    const removeHeaderLine = (side: 'leftLines' | 'rightLines', index: number) => {
        setHeaderConfig(prev => {
            const lines = prev[side].filter((_, i) => i !== index);
            return { ...prev, [side]: lines.length > 0 ? lines : [{ ...emptyHeaderLine }] };
        });
    };

    // --- Footer line helpers ---
    const updateFooterLineField = (index: number, field: keyof FooterLine, value: any) => {
        setFooterConfig(prev => {
            const lines = [...prev.lines];
            lines[index] = { ...lines[index], [field]: value };
            return { ...prev, lines };
        });
    };

    const addFooterLine = () => {
        setFooterConfig(prev => ({ ...prev, lines: [...prev.lines, { ...emptyFooterLine }] }));
    };

    const removeFooterLine = (index: number) => {
        setFooterConfig(prev => {
            const lines = prev.lines.filter((_, i) => i !== index);
            return { ...prev, lines: lines.length > 0 ? lines : [{ ...emptyFooterLine }] };
        });
    };

    // --- Render a styled-line editor row ---
    const renderStyledLineInputs = (
        lines: (HeaderLine | FooterLine)[],
        onChangeText: (i: number, v: string) => void,
        onChangeSize: (i: number, v: number) => void,
        onChangeBold: (i: number, v: boolean) => void,
        onRemove: (i: number) => void,
        onAdd: () => void,
        dir?: 'rtl' | 'ltr',
        placeholder?: string
    ) => (
        <div>
            {lines.map((line, i) => (
                <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                    <Input
                        value={line.text}
                        onChange={e => onChangeText(i, e.target.value)}
                        placeholder={placeholder || `Line ${i + 1}`}
                        dir={dir}
                        style={{ flex: 1 }}
                        size="small"
                    />
                    <InputNumber
                        min={8}
                        max={32}
                        value={line.fontSize}
                        onChange={v => onChangeSize(i, v || DEFAULT_FONT_SIZE)}
                        style={{ width: 54 }}
                        size="small"
                        title="Font size (px)"
                    />
                    <Button
                        size="small"
                        type={line.bold ? 'primary' : 'default'}
                        icon={<BoldOutlined />}
                        onClick={() => onChangeBold(i, !line.bold)}
                        title="Bold"
                        style={{ minWidth: 28 }}
                    />
                    {lines.length > 1 && (
                        <MinusCircleOutlined
                            style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 16 }}
                            onClick={() => onRemove(i)}
                        />
                    )}
                </div>
            ))}
            <Button
                type="dashed"
                onClick={onAdd}
                icon={<PlusOutlined />}
                size="small"
                style={{ width: '100%', marginTop: 4 }}
            >
                Add Line
            </Button>
        </div>
    );

    // Render a header line for the preview / print
    const previewLineStyle = (line: HeaderLine | FooterLine): React.CSSProperties => ({
        fontSize: (line.fontSize || DEFAULT_FONT_SIZE) * 0.7,
        fontWeight: line.bold ? 700 : 400,
        margin: '1px 0',
        lineHeight: 1.35,
    });

    return (
        <Row gutter={[24, 24]}>
            {/* Controls Column */}
            <Col xs={24} md={10}>
                {canUseOwnerPrintSettings && (
                    <Card title="Owner Print Settings" style={{ marginBottom: 16 }}>
                        <Form.Item label="Use owner print settings" style={{ marginBottom: 0 }}>
                            <Space>
                                <Switch
                                    checked={useOwnerPrintSettings}
                                    onChange={handleUseOwnerPrintSettingsChange}
                                />
                                <Text type="secondary">
                                    {useOwnerPrintSettings
                                        ? 'Enabled. Prescriptions will use the clinic owner print setup.'
                                        : 'Disabled. This doctor can use custom print setup.'}
                                </Text>
                            </Space>
                        </Form.Item>
                    </Card>
                )}

                {!useOwnerPrintSettings && (
                    <>
                <Card title="Print Settings">
                    <Form layout="vertical">
                        <Form.Item label="Paper Size">
                            <Select
                                value={previewSettings.paperSize}
                                onChange={val => handleSettingChange('paperSize', val)}
                            >
                                <Option value="a4">A4 (Standard)</Option>
                                <Option value="a5">A5 (Half Letter)</Option>
                                <Option value="custom">Custom (Printer Default)</Option>
                            </Select>
                        </Form.Item>

                        <Divider>Margins & Visibility</Divider>

                        <Form.Item label={`Top Margin: ${previewSettings.marginTop}mm`}>
                            <Slider min={0} max={290} value={previewSettings.marginTop} onChange={v => handleSettingChange('marginTop', v)} />
                            <Text type="secondary">Push content down below your pre-printed area.</Text>
                        </Form.Item>

                        <Form.Item label={`Left Margin: ${previewSettings.marginLeft}mm`}>
                            <Slider min={0} max={150} value={previewSettings.marginLeft} onChange={v => handleSettingChange('marginLeft', v)} />
                        </Form.Item>

                        <Form.Item label={`Right Margin: ${previewSettings.marginRight}mm`}>
                            <Slider min={0} max={150} value={previewSettings.marginRight} onChange={v => handleSettingChange('marginRight', v)} />
                        </Form.Item>

                        <Form.Item label="Show Header">
                            <Space>
                                <Switch checked={previewSettings.showHeader} onChange={c => handleSettingChange('showHeader', c)} />
                                <Text>{previewSettings.showHeader ? 'Visible' : 'Hidden (Pre-printed Paper)'}</Text>
                            </Space>
                        </Form.Item>

                        <Form.Item label="Show Footer">
                            <Space>
                                <Switch checked={previewSettings.showFooter} onChange={c => handleSettingChange('showFooter', c)} />
                                <Text>{previewSettings.showFooter ? 'Visible' : 'Hidden'}</Text>
                            </Space>
                        </Form.Item>

                        <Form.Item label="Print Patient Info (Name & Date)">
                            <Space>
                                <Switch checked={previewSettings.showPatientInfo} onChange={c => handleSettingChange('showPatientInfo', c)} />
                                <Text>{previewSettings.showPatientInfo ? 'Auto (Printed)' : 'Manual (Clean)'}</Text>
                            </Space>
                        </Form.Item>

                        <Form.Item label="Physician/PT Print Orientation">
                            <Select
                                value={previewSettings.physicianPrintOrientation}
                                onChange={val => handleSettingChange('physicianPrintOrientation', val)}
                            >
                                <Option value="landscape">Landscape</Option>
                                <Option value="portrait">Portrait</Option>
                            </Select>
                        </Form.Item>

                        <Divider>Print Language</Divider>

                        <Form.Item label="Print Labels Language">
                            <Space>
                                <Switch checked={previewSettings.printLocale === 'en'} onChange={c => handleSettingChange('printLocale', c ? 'en' : 'ar')} />
                                <Text>{previewSettings.printLocale === 'en' ? 'English' : 'Arabic (عربي)'}</Text>
                            </Space>
                        </Form.Item>

                        <Divider>Local only (this browser)</Divider>

                        <Form.Item
                            label="Arabic patient line & medicine frequency on English prints"
                            extra="When the dashboard language is English, prescriptions still print mostly in English, but the patient block uses Arabic labels (name, age, address, visit type, date) and each drug’s detail line (frequency, duration, timing, route) is printed in Arabic. Not saved to the server."
                        >
                            <Space>
                                <Switch
                                    checked={localArPatientFreqOnEnPrint}
                                    onChange={(c) => {
                                        setRxPrintArPatientFreqOnEnPrescription(c);
                                        setLocalArPatientFreqOnEnPrint(c);
                                    }}
                                />
                                <Text type="secondary">{localArPatientFreqOnEnPrint ? 'Enabled' : 'Disabled'}</Text>
                            </Space>
                        </Form.Item>

                        <Form.Item label="Preview Background (Not Printed)">
                            <Upload beforeUpload={handleImageUpload} maxCount={1} accept="image/*" showUploadList={false}>
                                <Button icon={<UploadOutlined />}>Upload Prescription Paper Image</Button>
                            </Upload>
                        </Form.Item>
                    </Form>
                </Card>

                {/* ---- Custom Prescription Header ---- */}
                <Card
                    title="Custom Prescription Header"
                    style={{ marginTop: 16 }}
                    extra={
                        <Switch checked={headerConfig.enabled} onChange={c => setHeaderConfig(p => ({ ...p, enabled: c }))} checkedChildren="ON" unCheckedChildren="OFF" />
                    }
                >
                    {headerConfig.enabled ? (
                        <>
                            <Form.Item label={`Header Height: ${headerConfig.headerHeight ? `${headerConfig.headerHeight}px` : 'Auto'}`}>
                                <Slider
                                    min={0}
                                    max={420}
                                    value={headerConfig.headerHeight}
                                    onChange={v => setHeaderConfig(p => ({ ...p, headerHeight: v }))}
                                />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col span={10}>
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>Left Column (EN)</Text>
                                    {renderStyledLineInputs(
                                        headerConfig.leftLines,
                                        (i, v) => updateHeaderLineField('leftLines', i, 'text', v),
                                        (i, v) => updateHeaderLineField('leftLines', i, 'fontSize', v),
                                        (i, v) => updateHeaderLineField('leftLines', i, 'bold', v),
                                        i => removeHeaderLine('leftLines', i),
                                        () => addHeaderLine('leftLines'),
                                        'ltr', 'e.g. Dr. Name'
                                    )}
                                </Col>
                                <Col span={4} style={{ textAlign: 'center' }}>
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>Badge</Text>
                                    {headerConfig.centerBadgeBase64 ? (
                                        <div>
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <Image
                                                    src={headerConfig.centerBadgeBase64}
                                                    width={60}
                                                    preview={false}
                                                    style={{ borderRadius: 4, border: '1px solid #d9d9d9' }}
                                                />
                                                <DeleteOutlined
                                                    style={{
                                                        position: 'absolute', top: -8, right: -8,
                                                        color: '#ff4d4f', cursor: 'pointer',
                                                        background: '#fff', borderRadius: '50%',
                                                        padding: 2, fontSize: 14,
                                                        boxShadow: '0 0 4px rgba(0,0,0,.15)'
                                                    }}
                                                    onClick={() => setHeaderConfig(p => ({ ...p, centerBadgeBase64: '' }))}
                                                />
                                            </div>
                                            <div style={{ marginTop: 8 }}>
                                                <Text type="secondary" style={{ fontSize: 11 }}>Logo Width</Text>
                                                <Slider
                                                    min={30}
                                                    max={700}
                                                    value={headerConfig.centerBadgeWidth}
                                                    onChange={v => setHeaderConfig(p => ({ ...p, centerBadgeWidth: v, centerBadgeSize: v }))}
                                                />
                                                <Text type="secondary" style={{ fontSize: 11 }}>Logo Height (0 = auto)</Text>
                                                <Slider
                                                    min={0}
                                                    max={420}
                                                    value={headerConfig.centerBadgeHeight}
                                                    onChange={v => setHeaderConfig(p => ({ ...p, centerBadgeHeight: v }))}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <Upload beforeUpload={processAndSetBadge} maxCount={1} accept="image/*" showUploadList={false}>
                                            <Button icon={<UploadOutlined />} size="small">Upload</Button>
                                        </Upload>
                                    )}
                                </Col>
                                <Col span={10}>
                                    <Text strong style={{ display: 'block', marginBottom: 6 }}>Right Column (AR)</Text>
                                    {renderStyledLineInputs(
                                        headerConfig.rightLines,
                                        (i, v) => updateHeaderLineField('rightLines', i, 'text', v),
                                        (i, v) => updateHeaderLineField('rightLines', i, 'fontSize', v),
                                        (i, v) => updateHeaderLineField('rightLines', i, 'bold', v),
                                        i => removeHeaderLine('rightLines', i),
                                        () => addHeaderLine('rightLines'),
                                        'rtl', 'مثال: د. الاسم'
                                    )}
                                </Col>
                            </Row>
                            <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                                Tip: Adjust the number next to each line to set its font size (px). Click <BoldOutlined /> to toggle bold.
                            </Text>
                        </>
                    ) : (
                        <Text type="secondary">Enable to add a custom 3-column header (English left, badge center, Arabic right).</Text>
                    )}
                </Card>

                {/* ---- Custom Prescription Footer ---- */}
                <Card
                    title="Custom Prescription Footer"
                    style={{ marginTop: 16 }}
                    extra={
                        <Switch checked={footerConfig.enabled} onChange={c => setFooterConfig(p => ({ ...p, enabled: c }))} checkedChildren="ON" unCheckedChildren="OFF" />
                    }
                >
                    {footerConfig.enabled ? (
                        <div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                Each line will be centered at the bottom of the prescription.
                            </Text>
                            {renderStyledLineInputs(
                                footerConfig.lines,
                                (i, v) => updateFooterLineField(i, 'text', v),
                                (i, v) => updateFooterLineField(i, 'fontSize', v),
                                (i, v) => updateFooterLineField(i, 'bold', v),
                                removeFooterLine,
                                addFooterLine,
                                'rtl', 'Footer line'
                            )}
                        </div>
                    ) : (
                        <Text type="secondary">Enable to add centered footer lines at the bottom of the prescription.</Text>
                    )}
                </Card>

                {/* ---- Custom Prescription CSS ---- */}
                <Card title="Custom Prescription Style" style={{ marginTop: 16 }}>
                    <Form layout="vertical">
                        <Form.Item
                            label="Full custom HTML template"
                            extra="Upload a complete prescription HTML template with placeholders like {{patientName}}, {{diagnosis}}, and {{program}}. Use this for clinic-specific templates such as Zayed."
                        >
                            <Upload beforeUpload={handleCustomHtmlUpload} maxCount={1} accept=".html,.htm,.txt" showUploadList={false}>
                                <Button icon={<UploadOutlined />}>Upload HTML Template</Button>
                            </Upload>
                        </Form.Item>
                        <Form.Item label="Custom HTML Template">
                            <TextArea
                                rows={8}
                                value={customHtml}
                                onChange={e => setCustomHtml(e.target.value)}
                                placeholder="<!DOCTYPE html><html>... {{patientName}} ...</html>"
                            />
                        </Form.Item>
                        <Divider />
                        <Form.Item
                            label="CSS / HTML style file"
                            extra="Upload CSS, or HTML that contains a <style> block. It applies to prescription printing only."
                        >
                            <Upload beforeUpload={handleCustomCssUpload} maxCount={1} accept=".css,.html,.htm,.txt" showUploadList={false}>
                                <Button icon={<UploadOutlined />}>Upload Style File</Button>
                            </Upload>
                        </Form.Item>
                        <Form.Item label="Custom CSS">
                            <TextArea
                                rows={6}
                                value={customCss}
                                onChange={e => setCustomCss(e.target.value)}
                                placeholder=".rx-header { min-height: 90px; }"
                            />
                        </Form.Item>
                    </Form>
                </Card>

                <Button type="primary" onClick={handleSave} block size="large" style={{ marginTop: 16 }}>
                    Save All Settings
                </Button>
                    </>
                )}
            </Col>

            {/* Preview Column */}
            {!useOwnerPrintSettings && (
            <Col xs={24} md={14}>
                <Card
                    title="Visual Preview"
                    extra={
                        <Select
                            value={previewRxTemplate}
                            onChange={v => setPreviewRxTemplate(v)}
                            style={{ width: 220 }}
                            size="small"
                        >
                            <Option value="standard">Standard prescription</Option>
                            <Option value="obgyn">OB/GYN (نسا وتوليد)</Option>
                        </Select>
                    }
                    bodyStyle={{ padding: 0, overflow: 'hidden', background: '#e6f7ff' }}
                >
                    <div style={{ position: 'relative', width: '100%', height: '700px', overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        {/* Simulated Paper */}
                        <div style={{
                            width: previewSettings.paperSize === 'a5' ? '148mm' : '210mm',
                            height: previewSettings.paperSize === 'a5' ? '210mm' : '297mm',
                            background: 'white',
                            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                            position: 'relative',
                            backgroundImage: backgroundObjectUrl ? `url(${backgroundObjectUrl})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: 'scale(0.8)',
                            transformOrigin: 'top center',
                            transition: 'all 0.3s',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>

                            {/* ====== Custom header — full paper width, above the margin-constrained body ====== */}
                            {previewSettings.showHeader && headerConfig.enabled && (
                                <div style={{
                                    marginTop: `${(previewSettings.marginTop || 0) + 10}px`,
                                    padding: '6px 12px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #ddd',
                                    background: 'rgba(255,255,255,0.9)',
                                    flexShrink: 0,
                                    minHeight: headerConfig.headerHeight ? `${headerConfig.headerHeight}px` : undefined,
                                }}>
                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                        {headerConfig.leftLines.filter(l => l.text.trim()).map((line, i) => (
                                            <div key={i} style={previewLineStyle(line)}>{line.text}</div>
                                        ))}
                                    </div>
                                    {headerConfig.centerBadgeBase64 && (
                                        <div style={{ flex: '0 0 auto', padding: '0 8px', textAlign: 'center' }}>
                                            <img
                                                src={headerConfig.centerBadgeBase64}
                                                alt="badge"
                                                style={{
                                                    width: headerConfig.centerBadgeWidth * 0.6,
                                                    height: headerConfig.centerBadgeHeight ? headerConfig.centerBadgeHeight * 0.6 : 'auto',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div style={{ flex: 1, textAlign: 'right', direction: 'rtl' }}>
                                        {headerConfig.rightLines.filter(l => l.text.trim()).map((line, i) => (
                                            <div key={i} style={previewLineStyle(line)}>{line.text}</div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ====== Body content — margin-constrained ====== */}
                            <div style={{
                                paddingTop: previewSettings.showHeader && headerConfig.enabled
                                    ? '10px'
                                    : `${(previewSettings.marginTop || 0) + 20}px`,
                                paddingLeft: `${(previewSettings.marginLeft || 0) + 20}px`,
                                paddingRight: `${(previewSettings.marginRight || 0) + 20}px`,
                                paddingBottom: '20px',
                                flex: 1,
                            }}>

                                {/* Default system header (only when custom is OFF) */}
                                {previewSettings.showHeader && !headerConfig.enabled && (
                                    <div style={{ textAlign: 'center', borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 20, background: 'rgba(255,255,255,0.8)' }}>
                                        <Title level={4} style={{ margin: 0 }}>{settings.clinicName || 'Clinic Name'}</Title>
                                        <Text>{settings.doctorTitle || 'Doctor Title'}</Text>
                                    </div>
                                )}

                                {/* Simulated Patient Info */}
                                {previewSettings.showPatientInfo && (
                                    <div style={{ background: 'rgba(240,240,240,0.7)', padding: 10, borderRadius: 4, marginBottom: 20 }}>
                                        <Text strong>Patient Name: </Text> <Text>Ahmed Mohamed</Text><br />
                                        <Text strong>Date: </Text> <Text>2023-10-25</Text>
                                    </div>
                                )}

                                {/* Simulated prescription body: standard vs OB/GYN */}
                                {previewRxTemplate === 'standard' ? (
                                    <div>
                                        <Title level={5}>Rx</Title>
                                        {[1, 2, 3].map(i => (
                                            <div key={i} style={{ borderBottom: '1px solid #eee', padding: '5px 0' }}>
                                                <Text strong>Drug Name {i}</Text><br />
                                                <Text type="secondary">1 قرص - 3 مرات يومياً - بعد الأكل</Text>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontSize: 11, color: '#c2185b', marginBottom: 8, fontWeight: 600 }}>
                                            OB/GYN — sample layout (matches printed gynecology prescription)
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                            <div style={{
                                                flex: '0 0 32%',
                                                maxWidth: '32%',
                                                border: '1px solid #f48fb1',
                                                borderRadius: 6,
                                                padding: 8,
                                                background: 'rgba(255, 245, 248, 0.95)',
                                                fontSize: 10,
                                                lineHeight: 1.45,
                                            }}>
                                                <div style={{ fontWeight: 700, color: '#ad1457', marginBottom: 6, borderBottom: '1px solid #f8bbd0', paddingBottom: 4 }}>
                                                    history / التاريخ المرضي
                                                </div>
                                                <div><Text strong>Married since:</Text> —</div>
                                                <div><Text strong>GPLAD:</Text> G2 P1 L1 A0 D0</div>
                                                <div><Text strong>Delivery:</Text> Normal</div>
                                                <div><Text strong>LMP / EDD:</Text> — / —</div>
                                                <div style={{ marginTop: 6 }}><Text strong>Past history:</Text> …</div>
                                                <div><Text strong>Blood group:</Text> O+</div>
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0, border: '1px solid #eee', borderRadius: 6, padding: 8, background: 'rgba(255,255,255,0.95)' }}>
                                                <div style={{ fontWeight: 700, marginBottom: 8, color: '#333' }}>Medications / الأدوية</div>
                                                {[1, 2].map(i => (
                                                    <div key={i} style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 0' }}>
                                                        <Text strong>Drug {i}</Text>
                                                        <div><Text type="secondary" style={{ fontSize: 10 }}>1×3 — بعد الأكل</Text></div>
                                                    </div>
                                                ))}
                                                <div style={{ marginTop: 8, fontSize: 10, color: '#666' }}>
                                                    <Text strong>Notes:</Text> …
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{
                                            marginTop: 12,
                                            border: '1px dashed #f48fb1',
                                            borderRadius: 6,
                                            padding: 8,
                                            background: 'rgba(255, 250, 252, 0.95)',
                                        }}>
                                            <Text strong style={{ fontSize: 11, color: '#ad1457' }}>Investigations / الفحوصات</Text>
                                            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {['CBC', 'Urine', 'US Pelvis'].map(x => (
                                                    <span key={x} style={{
                                                        fontSize: 9,
                                                        padding: '2px 8px',
                                                        borderRadius: 4,
                                                        background: '#fce4ec',
                                                        border: '1px solid #f8bbd0',
                                                    }}>{x}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ====== Footer — full paper width, pinned to bottom ====== */}
                            {previewSettings.showFooter && (
                                footerConfig.enabled && footerConfig.lines.some(l => l.text.trim()) ? (
                                    <div style={{
                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                        textAlign: 'center', borderTop: '1px solid #ddd',
                                        padding: '8px 12px',
                                        background: 'rgba(255,255,255,0.9)'
                                    }}>
                                        {footerConfig.lines.filter(l => l.text.trim()).map((line, i) => (
                                            <div key={i} style={{
                                                fontSize: (line.fontSize || 12) * 0.7,
                                                fontWeight: line.bold ? 700 : 400,
                                                margin: '2px 0',
                                                lineHeight: 1.4,
                                                direction: 'rtl'
                                            }}>{line.text}</div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{
                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                        textAlign: 'center', borderTop: '1px solid #eee',
                                        padding: '10px 20px',
                                        background: 'rgba(255,255,255,0.8)'
                                    }}>
                                        <Text type="secondary">{settings.receiptFooter || 'Get well soon!'}</Text>
                                    </div>
                                )
                            )}

                            {/* Guide lines */}
                            <div style={{ position: 'absolute', top: `${(previewSettings.marginTop / 297 * 100)}%`, left: 0, right: 0, borderTop: '2px dashed red', pointerEvents: 'none', opacity: 0.5 }}>
                                <span style={{ background: 'red', color: 'white', fontSize: 10, padding: 2 }}>Top</span>
                            </div>
                            {previewSettings.marginLeft > 0 && (
                                <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${previewSettings.marginLeft}px`, borderLeft: '2px dashed blue', pointerEvents: 'none', opacity: 0.5 }}>
                                    <span style={{ background: 'blue', color: 'white', fontSize: 10, padding: 2 }}>L</span>
                                </div>
                            )}
                            {previewSettings.marginRight > 0 && (
                                <div style={{ position: 'absolute', top: 0, bottom: 0, right: `${previewSettings.marginRight}px`, borderRight: '2px dashed green', pointerEvents: 'none', opacity: 0.5 }}>
                                    <span style={{ background: 'green', color: 'white', fontSize: 10, padding: 2, position: 'absolute', right: 0 }}>R</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ padding: 16, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                        <Text type="secondary"><FileImageOutlined /> The uploaded image is for preview only and will not be printed.</Text>
                    </div>
                </Card>
            </Col>
            )}
        </Row>
    );
};

export default PrescriptionLayoutEditor;
